import type { ReactNode } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  useCreateVisitMutation,
  useGenerateCodingMutation,
  useGenerateSummaryMutation,
  useLazyGetTranscriptQuery,
} from "./state/dentist/api";
import { useMediaRecorder } from "./hooks/useMediaRecorder";
import { useWebSocket } from "./hooks/useWebSocket";
import type { TranscriptSegment } from "./types";


type VisitState = "idle" | "recording" | "processing" | "done";

interface ParsedSummary {
  summary: string;
  doctorTodos: string[];
  patientTodos: string;
}

const DENTAL_KEYWORDS = new Set([
  // Teeth & anatomy
  "tooth", "teeth", "molar", "molars", "premolar", "premolars", "incisor", "incisors",
  "canine", "canines", "bicuspid", "bicuspids", "wisdom", "enamel", "dentin", "pulp",
  "root", "roots", "crown", "gum", "gums", "gingiva", "jaw", "mandible", "maxilla",
  // Conditions
  "cavity", "cavities", "caries", "decay", "abscess", "infection", "inflammation",
  "gingivitis", "periodontitis", "plaque", "tartar", "calculus", "recession",
  "fracture", "crack", "erosion", "lesion", "occlusion", "malocclusion",
  "sensitivity", "swelling", "bleeding", "pain",
  // Procedures & treatments
  "filling", "fillings", "extraction", "implant", "implants", "veneer", "veneers",
  "bridge", "denture", "dentures", "braces", "retainer", "aligners",
  "cleaning", "scaling", "polishing", "whitening", "bleaching",
  "bonding", "sealant", "sealants", "fluoride",
  "x-ray", "x-rays", "xray", "xrays", "radiograph",
  // Materials & tools
  "composite", "amalgam", "porcelain", "ceramic", "resin", "zirconia",
  // Clinical terms
  "bite", "occlusal", "buccal", "lingual", "mesial", "distal", "proximal",
  "anterior", "posterior", "periodontal", "endodontic", "orthodontic", "prosthodontic",
  "anesthesia", "anesthetic", "numbing",
]);

const MULTI_WORD_KEYWORDS = [
  "root canal", "wisdom tooth", "wisdom teeth", "dental implant", "dental implants",
  "gum disease", "tooth decay", "dry socket",
];

const KEYWORD_REGEX: RegExp | null = (() => {
  try {
    const esc = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const multiPattern = MULTI_WORD_KEYWORDS.map(esc).join("|");
    const singlePattern = Array.from(DENTAL_KEYWORDS).map(esc).join("|");
    return new RegExp(`\\b(${multiPattern}|${singlePattern})\\b`, "gi");
  } catch {
    return null;
  }
})();

function highlightKeywords(text: string): ReactNode[] {
  if (!KEYWORD_REGEX || !text) {
    return [text || ""];
  }

  try {
    KEYWORD_REGEX.lastIndex = 0;

    const result: ReactNode[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = KEYWORD_REGEX.exec(text)) !== null) {
      if (match.index > lastIndex) {
        result.push(text.slice(lastIndex, match.index));
      }
      result.push(
        <span key={match.index} className="dt-kw">
          {match[0]}
        </span>,
      );
      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < text.length) {
      result.push(text.slice(lastIndex));
    }

    return result.length > 0 ? result : [text];
  } catch (e) {
    console.error("highlightKeywords failed:", e);
    return [text];
  }
}

function parseSummaryResponse(raw: string): ParsedSummary {
  const sections: Record<string, string> = {};
  let currentKey = "";

  for (const line of raw.split("\n")) {
    const headerMatch = line.match(/^##\s+(.+)/);
    if (headerMatch) {
      currentKey = headerMatch[1].trim().toLowerCase();
      sections[currentKey] = "";
    } else if (currentKey) {
      sections[currentKey] += (sections[currentKey] ? "\n" : "") + line;
    }
  }

  const summary = (sections["summary"] ?? "").trim();

  const doctorTodos = (sections["doctor's to-dos"] ?? sections["doctor to-dos"] ?? "")
    .split("\n")
    .map((l) => l.replace(/^-\s*/, "").trim())
    .filter((l) => l && l.toLowerCase() !== "none");

  const patientTodosRaw = (sections["patient's to-dos"] ?? sections["patient to-dos"] ?? "").trim();
  const patientTodos = patientTodosRaw.toLowerCase() === "none" ? "" : patientTodosRaw;

  return { summary, doctorTodos, patientTodos };
}

const styles = `
  .dt-root {
    --gold:         #F5C518;
    --gold-dim:     #B8940C;
    --gold-bg:      #FEFBEE;
    --gold-border:  #E8D068;

    --ink:          #111111;
    --ink-2:        #2E2E2E;
    --ink-3:        #5A5A5A;
    --ink-4:        #9A9A9A;

    --bg:           #F4F3EF;
    --surface:      #FFFFFF;
    --rule:         #E4E2DA;
    --rule-strong:  #C8C6BC;

    --kw-color:     #111111;
    --kw-bg:        rgba(245, 197, 24, 0.35);

    --r: 10px;
    --r-sm: 6px;

    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    background: var(--bg);
    color: var(--ink);
    height: 100vh;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    -webkit-font-smoothing: antialiased;
  }

  .dt-root *, .dt-root *::before, .dt-root *::after {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  .dt-header {
    height: 60px;
    background: var(--surface);
    border-bottom: 1px solid var(--rule);
    display: flex;
    align-items: center;
    padding: 0 32px;
    gap: 20px;
    flex-shrink: 0;
  }
  .dt-logo { display: flex; align-items: center; gap: 10px; }
  .dt-logo-bars { display: flex; align-items: flex-end; gap: 2.5px; }
  .dt-lb { width: 4px; border-radius: 2px; background: var(--gold); }
  .dt-lb1{height:9px} .dt-lb2{height:16px} .dt-lb3{height:11px} .dt-lb4{height:20px} .dt-lb5{height:13px}
  .dt-logo-name { font-size: 17px; font-weight: 700; letter-spacing: -0.4px; }
  .dt-logo-sep { width: 1px; height: 16px; background: var(--rule-strong); }
  .dt-logo-sub { font-size: 12px; font-weight: 500; color: var(--ink-4); letter-spacing: 0.05em; text-transform: uppercase; }
  .dt-h-fill { flex: 1; }
  .dt-session-meta { font-size: 15px; color: var(--ink-3); display: flex; align-items: center; gap: 10px; }
  .dt-session-meta .dt-dot { color: var(--rule-strong); }
  .dt-lang-seg { display: flex; border: 1px solid var(--rule-strong); border-radius: var(--r-sm); overflow: hidden; }
  .dt-lang-btn {
    padding: 6px 15px; font-family: inherit; font-size: 13px; font-weight: 600;
    border: none; background: transparent; color: var(--ink-4); cursor: pointer; transition: all .12s;
  }
  .dt-lang-btn.active { background: var(--ink); color: #fff; }
  .dt-btn-start {
    padding: 10px 24px; border: none; border-radius: 20px; background: var(--gold);
    color: var(--ink); font-family: inherit; font-size: 15px; font-weight: 700;
    cursor: pointer; letter-spacing: -0.2px; transition: opacity .12s;
    display: flex; align-items: center; gap: 9px;
  }
  .dt-btn-start:hover { opacity: .85; }
  .dt-rec { width: 8px; height: 8px; border-radius: 50%; background: #D0260E; animation: dt-blink 1.5s ease-in-out infinite; }
  @keyframes dt-blink { 0%,100%{opacity:1} 50%{opacity:.2} }

  .dt-layout {
    display: grid;
    grid-template-columns: 1fr 440px;
    flex: 1;
    overflow: hidden;
  }

  .dt-tx-panel {
    display: flex; flex-direction: column; overflow: hidden;
    border-right: 1px solid var(--rule); background: var(--bg);
  }
  .dt-tx-top {
    padding: 24px 32px 16px;
    display: flex; align-items: baseline; gap: 14px;
    flex-shrink: 0;
  }
  .dt-tx-title { font-size: 24px; font-weight: 700; letter-spacing: -0.5px; }
  .dt-tx-meta { font-size: 15px; color: var(--ink-4); }

  .dt-copy-bar { padding: 0 32px 14px; flex-shrink: 0; }
  .dt-copy-btn-sm {
    display: inline-flex; align-items: center; gap: 6px;
    font-family: inherit; font-size: 13px; font-weight: 600;
    color: var(--ink-3); background: var(--surface);
    border: 1px solid var(--rule-strong); border-radius: var(--r-sm);
    padding: 6px 14px; cursor: pointer; transition: all .12s;
  }
  .dt-copy-btn-sm:hover { border-color: var(--gold-dim); color: var(--ink); }

  .dt-tx-scroll {
    flex: 1; overflow-y: auto;
    padding: 0 32px 32px;
    display: flex; flex-direction: column;
  }
  .dt-tx-scroll::-webkit-scrollbar { width: 4px; }
  .dt-tx-scroll::-webkit-scrollbar-thumb { background: var(--rule-strong); border-radius: 2px; }

  .dt-ts-divider {
    display: flex; align-items: center; gap: 12px;
    padding: 8px 0 4px;
  }
  .dt-ts-line { flex: 1; height: 1px; background: var(--rule); }
  .dt-ts-lbl { font-size: 12px; font-weight: 600; color: var(--ink-4); letter-spacing: 0.06em; }

  .dt-utt {
    display: grid;
    grid-template-columns: 80px 1fr;
    gap: 0 18px;
    padding: 13px 0;
    border-bottom: 1px solid var(--rule);
  }
  .dt-utt:last-child { border-bottom: none; }
  .dt-utt-meta {
    display: flex; flex-direction: column;
    align-items: flex-end; padding-top: 3px; gap: 5px;
  }
  .dt-spk {
    font-size: 11px; font-weight: 700; letter-spacing: 0.06em;
    text-transform: uppercase; padding: 2px 8px; border-radius: 3px; white-space: nowrap;
  }
  .dt-spk.dr { color: var(--ink); background: var(--gold); }
  .dt-spk.pt { color: var(--ink-3); background: var(--rule); }
  .dt-utt-time { font-size: 12px; color: var(--ink-4); font-variant-numeric: tabular-nums; }
  .dt-utt-text { font-size: 18px; line-height: 1.7; padding-top: 1px; }
  .dt-utt.doctor .dt-utt-text { color: var(--ink); }
  .dt-utt.patient .dt-utt-text { color: var(--ink-3); font-size: 17px; }
  .dt-kw { display: inline; padding: 0 4px; border-radius: 4px; font-weight: inherit; color: var(--kw-color); background: var(--kw-bg); }

  .dt-empty-state {
    flex: 1; display: flex; flex-direction: column;
    align-items: center; justify-content: center; gap: 12px;
  }

  .dt-sidebar {
    display: flex; flex-direction: column; overflow: hidden;
    background: var(--surface); border-left: 1px solid var(--rule);
  }
  .dt-sb-head { padding: 24px 26px 0; flex-shrink: 0; }
  .dt-sb-head-row { display: flex; align-items: baseline; gap: 12px; margin-bottom: 18px; }
  .dt-sb-title { font-size: 24px; font-weight: 700; letter-spacing: -0.5px; }
  .dt-ai-chip {
    font-size: 11px; font-weight: 700; letter-spacing: 0.05em;
    color: var(--gold-dim); background: var(--gold-bg);
    border: 1px solid var(--gold-border); padding: 2px 8px; border-radius: 3px;
  }

  .dt-sb-scroll {
    flex: 1; overflow-y: auto;
    padding: 0 26px 32px;
    display: flex; flex-direction: column; gap: 28px;
  }
  .dt-sb-scroll::-webkit-scrollbar { width: 4px; }
  .dt-sb-scroll::-webkit-scrollbar-thumb { background: var(--rule-strong); border-radius: 2px; }

  .dt-sb-section { display: flex; flex-direction: column; gap: 12px; }
  .dt-sb-section-head { display: flex; align-items: center; justify-content: space-between; }
  .dt-sb-section-lbl {
    font-size: 12px; font-weight: 700; letter-spacing: 0.07em;
    text-transform: uppercase; color: var(--ink-4);
  }

  .dt-copy-inline {
    font-family: inherit; font-size: 12px; font-weight: 600;
    color: var(--ink-4); background: none; border: none; cursor: pointer;
    padding: 2px 6px; border-radius: 4px; transition: all .12s;
  }
  .dt-copy-inline:hover { color: var(--ink); background: var(--bg); }

  .dt-summary-p { font-size: 17px; line-height: 1.75; color: var(--ink-2); }

  .dt-todo-list { list-style: none; }
  .dt-todo-item {
    display: flex; align-items: flex-start; gap: 13px;
    padding: 11px 0; border-bottom: 1px solid var(--rule);
    cursor: pointer; user-select: none;
  }
  .dt-todo-item:last-child { border-bottom: none; }
  .dt-chk {
    width: 20px; height: 20px; border: 1.5px solid var(--rule-strong);
    border-radius: 4px; flex-shrink: 0; margin-top: 2px; background: var(--surface);
    display: flex; align-items: center; justify-content: center; transition: all .12s;
  }
  .dt-chk.done { background: var(--gold); border-color: var(--gold-dim); }
  .dt-chk.done::after {
    content: ''; width: 9px; height: 5px;
    border-left: 1.5px solid var(--ink); border-bottom: 1.5px solid var(--ink);
    transform: rotate(-45deg) translateY(-1px);
  }
  .dt-todo-text { font-size: 16px; line-height: 1.55; color: var(--ink-2); flex: 1; }
  .dt-todo-text.done { color: var(--ink-4); text-decoration: line-through; }

  .dt-notes-ta {
    width: 100%; padding: 13px 14px;
    border: 1px solid var(--rule-strong); border-radius: var(--r-sm);
    font-family: inherit; font-size: 16px; line-height: 1.7;
    color: var(--ink); background: var(--bg); resize: none; outline: none;
    transition: border-color .12s;
  }
  .dt-notes-ta:focus { border-color: var(--gold-dim); background: var(--surface); }

  .dt-btn-send {
    width: 100%; padding: 13px 16px; border-radius: var(--r-sm);
    font-family: inherit; font-size: 15px; font-weight: 700;
    cursor: pointer; transition: all .12s; letter-spacing: -0.1px;
    display: flex; align-items: center; justify-content: center; gap: 8px;
  }
  .dt-btn-send:active { transform: scale(.98); }
  .dt-btn-fill { background: var(--ink); color: #fff; border: none; }
  .dt-btn-fill:hover { background: #2a2a2a; }
  .dt-btn-outline { background: transparent; color: var(--ink-2); border: 1.5px solid var(--rule-strong); }
  .dt-btn-outline:hover { border-color: var(--gold-dim); background: var(--gold-bg); }

  .dt-goz-codes { display: flex; flex-direction: column; gap: 0; }
  .dt-goz-row {
    display: flex; align-items: flex-start; gap: 14px;
    padding: 11px 0; border-bottom: 1px solid var(--rule);
  }
  .dt-goz-row:last-child { border-bottom: none; }
  .dt-goz-code {
    font-family: 'Menlo', 'Consolas', monospace;
    font-size: 13px; font-weight: 700;
    color: var(--ink); background: var(--kw-bg);
    padding: 3px 9px; border-radius: 4px; white-space: nowrap;
    min-width: 52px; text-align: center;
  }
  .dt-goz-desc { font-size: 16px; color: var(--ink-2); flex: 1; line-height: 1.4; }
  .dt-goz-reason { font-size: 13px; color: var(--ink-4); line-height: 1.5; padding: 2px 0 0; }
  .dt-goz-price {
    font-family: 'Menlo', 'Consolas', monospace;
    font-size: 15px; font-weight: 600; color: var(--ink); white-space: nowrap;
  }

  .dt-goz-total-row {
    display: flex; align-items: center; justify-content: space-between;
    padding: 13px 0 0; margin-top: 4px;
    border-top: 2px solid var(--rule-strong);
  }
  .dt-goz-total-lbl {
    font-size: 13px; font-weight: 700; letter-spacing: 0.04em;
    text-transform: uppercase; color: var(--ink-3);
  }
  .dt-goz-total-val {
    font-family: 'Menlo', 'Consolas', monospace;
    font-size: 18px; font-weight: 700; color: var(--ink);
  }

  .dt-goz-meta { display: flex; gap: 6px; flex-wrap: wrap; margin-top: 2px; }
  .dt-meta-pill {
    font-size: 13px; font-weight: 500; color: var(--ink-3);
    background: var(--bg); border: 1px solid var(--rule);
    padding: 3px 10px; border-radius: 20px;
  }

  .dt-goz-actions { display: flex; gap: 8px; margin-top: 4px; }
  .dt-btn-sm {
    flex: 1; padding: 10px; border-radius: var(--r-sm);
    font-family: inherit; font-size: 14px; font-weight: 600;
    cursor: pointer; transition: all .12s;
  }
  .dt-btn-sm-ghost {
    background: transparent; color: var(--ink-3);
    border: 1px solid var(--rule-strong);
  }
  .dt-btn-sm-ghost:hover { border-color: var(--ink-3); color: var(--ink); }
  .dt-btn-sm-gold {
    background: var(--gold); color: var(--ink);
    border: none; font-weight: 700;
  }
  .dt-btn-sm-gold:hover { opacity: .85; }

  .dt-rule { height: 1px; background: var(--rule); margin: 0; }
`;


export const DentistRoute = () => {
  const [todos, setTodos] = useState<boolean[]>([]);
  const [activeLang, setActiveLang] = useState<"EN" | "DE">("EN");
  const [copyStates, setCopyStates] = useState<Record<string, boolean>>({});
  const [visitState, setVisitState] = useState<VisitState>("idle");
  const [visitId, setVisitId] = useState<string | null>(null);

  const txScrollRef = useRef<HTMLDivElement>(null);

  const [createVisit] = useCreateVisitMutation();
  const [generateSummary, { isLoading: isGeneratingSummary }] = useGenerateSummaryMutation();
  const [generateCoding, { data: codingData, isLoading: isGeneratingCoding, reset: resetCoding }] =
    useGenerateCodingMutation();
  const [parsedSummary, setParsedSummary] = useState<ParsedSummary | null>(null);
  const { segments, connect, disconnect, sendAudio, clearSegments, setSegments } = useWebSocket();
  const { init: initRecording, startCapture, stop: stopRecording } = useMediaRecorder();
  const [getTranscript] = useLazyGetTranscriptQuery();

  // Pre-fetch previous visit data when visitId is in URL params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlVisitId = params.get("visit_id");
    if (!urlVisitId) return;

    setVisitId(urlVisitId);
    setVisitState("processing");

    const lang = activeLang.toLowerCase();

    const transcriptPromise = getTranscript(urlVisitId)
      .unwrap()
      .then((result) => {
        // Parse transcript string into segments for display
        const lines = result.transcript.split("\n").filter(Boolean);
        const parsed: TranscriptSegment[] = lines.map((line, i) => {
          const speakerMatch = line.match(/^(Doctor|Patient):\s*/i);
          if (speakerMatch) {
            const speaker = speakerMatch[1].toLowerCase() === "doctor" ? 0 : 1;
            const text = line.slice(speakerMatch[0].length);
            return { speaker, text, start: i, end: i, isFinal: true };
          }
          return { speaker: 0, text: line, start: i, end: i, isFinal: true };
        });
        setSegments(parsed);
      })
      .catch((err) => {
        console.error("Failed to fetch transcript:", err);
      });

    const summaryPromise = generateSummary({ visitId: urlVisitId, lang })
      .unwrap()
      .then((result) => {
        const parsed = parseSummaryResponse(result.summary);
        setParsedSummary(parsed);
        setTodos(parsed.doctorTodos.map(() => false));
      })
      .catch((err) => {
        console.error("Failed to generate summary:", err);
        setParsedSummary({
          summary: "Failed to generate summary. Please try again.",
          doctorTodos: [],
          patientTodos: "",
        });
      });

    const codingPromise = generateCoding({ visitId: urlVisitId, lang })
      .unwrap()
      .catch((err) => {
        console.error("Failed to generate coding:", err);
      });

    Promise.all([transcriptPromise, summaryPromise, codingPromise]).then(() => {
      setVisitState("done");
    });
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-scroll transcript to bottom as new segments arrive
  useEffect(() => {
    const el = txScrollRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [segments]);

  const handleStartVisit = useCallback(async () => {
    try {
      setParsedSummary(null);
      resetCoding();
      clearSegments();

      let newVisitId: string;
      try {
        const result = await createVisit().unwrap();
        newVisitId = result.visitId;
      } catch (err) {
        console.error("Failed to create visit:", err);
        alert("Failed to connect to backend. Is the server running on port 8000?");
        return;
      }
      setVisitId(newVisitId);

      const url = new URL(window.location.href);
      url.searchParams.set("visit_id", newVisitId);
      window.history.pushState({}, "", url.toString());

      try {
        await initRecording((blob) => {
          sendAudio(blob);
        });
      } catch (err) {
        console.error("Failed to initialize microphone:", err);
        alert("Failed to access microphone. Please check microphone permissions and try again.");
        return;
      }

      try {
        await connect(newVisitId, activeLang.toLowerCase());
      } catch (err) {
        console.error("Failed to connect WebSocket:", err);
        alert("Failed to connect to backend. Please try again.");
        stopRecording();
        return;
      }

      startCapture();
      setVisitState("recording");
    } catch (err) {
      console.error("Failed to start visit:", err);
      alert(`Failed to start visit: ${err}`);
    }
  }, [connect, initRecording, startCapture, sendAudio, clearSegments, activeLang, createVisit, disconnect, stopRecording, resetCoding]);

  const handleDone = useCallback(async () => {
    stopRecording();
    disconnect();
    setVisitState("processing");

    if (visitId) {
      const lang = activeLang.toLowerCase();

      const summaryPromise = generateSummary({ visitId, lang })
        .unwrap()
        .then((result) => {
          const parsed = parseSummaryResponse(result.summary);
          setParsedSummary(parsed);
          setTodos(parsed.doctorTodos.map(() => false));
        })
        .catch((err) => {
          console.error("Failed to generate summary:", err);
          setParsedSummary({
            summary: "Failed to generate summary. Please try again.",
            doctorTodos: [],
            patientTodos: "",
          });
        });

      const codingPromise = generateCoding({ visitId, lang })
        .unwrap()
        .catch((err) => {
          console.error("Failed to generate coding:", err);
        });

      await Promise.all([summaryPromise, codingPromise]);
    }

    setVisitState("done");
  }, [stopRecording, disconnect, visitId, generateSummary, generateCoding, activeLang]);

  const flash = useCallback((key: string) => {
    setCopyStates((prev) => ({ ...prev, [key]: true }));
    setTimeout(() => setCopyStates((prev) => ({ ...prev, [key]: false })), 1800);
  }, []);

  const copyTranscript = () => {
    const finalSegments = segments.filter((s) => s.isFinal);
    const text = finalSegments
      .map((s) => {
        const label = s.speaker != null ? (s.speaker === 0 ? "Doctor" : "Patient") + ": " : "";
        return `${label}${s.text}`;
      })
      .join("\n");
    navigator.clipboard.writeText(text);
    flash("transcript");
  };

  const copySection = (key: string, elementId: string) => {
    const el = document.getElementById(elementId);
    if (el) {
      navigator.clipboard.writeText(el.innerText.trim());
    }
    flash(key);
  };

  const copyTextarea = (key: string, elementId: string) => {
    const el = document.getElementById(elementId) as HTMLTextAreaElement | null;
    if (el) {
      navigator.clipboard.writeText(el.value.trim());
    }
    flash(key);
  };

  const toggleTodo = (index: number) => {
    setTodos((prev) => prev.map((v, i) => (i === index ? !v : v)));
  };

  return (
    <>
      <style>{styles}</style>
      <link
        href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap"
        rel="stylesheet"
      />
      <div className="dt-root">
        {/* Header */}
        <header className="dt-header">
          <div className="dt-logo">
            <div className="dt-logo-bars">
              <div className="dt-lb dt-lb1" />
              <div className="dt-lb dt-lb2" />
              <div className="dt-lb dt-lb3" />
              <div className="dt-lb dt-lb4" />
              <div className="dt-lb dt-lb5" />
            </div>
            <span className="dt-logo-name">Nimbus</span>
            <div className="dt-logo-sep" />
            <span className="dt-logo-sub">Dental</span>
          </div>
          <div className="dt-h-fill" />
          <div className="dt-session-meta">
            <span>Dr. Müller</span>
            <span className="dt-dot">·</span>
            <span>Zimmer 3</span>
            <span className="dt-dot">·</span>
            <span>10. März 2026</span>
          </div>
          <div style={{ width: 14 }} />
          <div className="dt-lang-seg">
            <button
              className={`dt-lang-btn ${activeLang === "EN" ? "active" : ""}`}
              onClick={() => setActiveLang("EN")}
            >
              EN
            </button>
            <button
              className={`dt-lang-btn ${activeLang === "DE" ? "active" : ""}`}
              onClick={() => setActiveLang("DE")}
            >
              DE
            </button>
          </div>
          <div style={{ width: 10 }} />
          <button
            className="dt-btn-start"
            style={visitState !== "recording" ? { background: "var(--ink)", color: "#fff" } : undefined}
            onClick={visitState === "recording" ? handleDone : handleStartVisit}
          >
            {visitState === "recording" ? (
              <>
                <span className="dt-rec" />
                Done
              </>
            ) : (
              "Start Visit"
            )}
          </button>
        </header>

        {/* Layout */}
        <div className="dt-layout">
          {/* Left: Transcript */}
          <div className="dt-tx-panel">
            <div className="dt-tx-top">
              <span className="dt-tx-title">Transcript</span>
              {segments.length > 0 && (
                <span className="dt-tx-meta">
                  {segments.filter((s) => s.isFinal).length} utterances
                  {visitState === "recording" && " · Live"}
                </span>
              )}
            </div>

            {segments.length > 0 && (
              <div className="dt-copy-bar">
                <button className="dt-copy-btn-sm" onClick={copyTranscript}>
                  {copyStates["transcript"] ? "Copied" : "Copy full transcript"}
                </button>
              </div>
            )}

            <div className="dt-tx-scroll" id="dt-tx-scroll" ref={txScrollRef}>
              {visitState === "idle" ? (
                <div className="dt-empty-state">
                  <span style={{ fontSize: 40, opacity: 0.4 }}>🎙</span>
                  <span className="dt-tx-meta">
                    Click "Start Visit" to begin recording
                  </span>
                </div>
              ) : segments.length === 0 ? (
                <div className="dt-empty-state">
                  <span className="dt-rec" />
                  <span className="dt-tx-meta">
                    {visitState === "processing" ? "Loading transcript…" : "Listening…"}
                  </span>
                </div>
              ) : (
                segments.map((seg, i) => {
                  const isDoctor = seg.speaker === 0;
                  return (
                    <div
                      key={i}
                      className={`dt-utt ${isDoctor ? "doctor" : "patient"}`}
                      style={{ opacity: seg.isFinal ? 1 : 0.5 }}
                    >
                      <div className="dt-utt-meta">
                        {seg.speaker != null && (
                          <span className={`dt-spk ${isDoctor ? "dr" : "pt"}`}>
                            {isDoctor ? "Doctor" : "Patient"}
                          </span>
                        )}
                      </div>
                      <div className="dt-utt-text">{highlightKeywords(seg.text)}</div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Right: Summary Sidebar */}
          <div className="dt-sidebar">
            <div className="dt-sb-head">
              <div className="dt-sb-head-row">
                <span className="dt-sb-title">Summary</span>
                <span className="dt-ai-chip">AI</span>
              </div>
            </div>

            <div className="dt-sb-scroll">
              {/* Visit Overview */}
              <div className="dt-sb-section">
                <div className="dt-sb-section-head">
                  <span className="dt-sb-section-lbl">Visit Overview</span>
                  {parsedSummary?.summary && (
                    <button
                      className="dt-copy-inline"
                      onClick={() => copySection("summary", "dt-summary-text")}
                    >
                      {copyStates["summary"] ? "Copied" : "Copy"}
                    </button>
                  )}
                </div>
                {isGeneratingSummary ? (
                  <p className="dt-summary-p" style={{ color: "var(--ink-4)" }}>
                    Generating summary…
                  </p>
                ) : parsedSummary?.summary ? (
                  <p className="dt-summary-p" id="dt-summary-text">
                    {parsedSummary.summary}
                  </p>
                ) : (
                  <p className="dt-summary-p" style={{ color: "var(--ink-4)" }}>
                    Summary will appear here after the visit ends.
                  </p>
                )}
              </div>

              <div className="dt-rule" />

              {/* Doctor To-Do */}
              <div className="dt-sb-section">
                <div className="dt-sb-section-head">
                  <span className="dt-sb-section-lbl">Doctor To-Do</span>
                  {parsedSummary && parsedSummary.doctorTodos.length > 0 && (
                    <button
                      className="dt-copy-inline"
                      onClick={() => copySection("todos", "dt-todo-list")}
                    >
                      {copyStates["todos"] ? "Copied" : "Copy"}
                    </button>
                  )}
                </div>
                {parsedSummary && parsedSummary.doctorTodos.length > 0 ? (
                  <>
                    <ul className="dt-todo-list" id="dt-todo-list">
                      {parsedSummary.doctorTodos.map((item, i) => (
                        <li key={i} className="dt-todo-item" onClick={() => toggleTodo(i)}>
                          <div className={`dt-chk ${todos[i] ? "done" : ""}`} />
                          <span className={`dt-todo-text ${todos[i] ? "done" : ""}`}>
                            {item}
                          </span>
                        </li>
                      ))}
                    </ul>
                    <button className="dt-btn-send dt-btn-fill" style={{ marginTop: 4 }}>
                      Send as Reminder
                    </button>
                  </>
                ) : (
                  <p className="dt-summary-p" style={{ color: "var(--ink-4)" }}>
                    {visitState === "done"
                      ? "No to-dos were identified for this visit."
                      : "To-dos will appear here after the visit ends."}
                  </p>
                )}
              </div>

              <div className="dt-rule" />

              {/* Patient Notes */}
              <div className="dt-sb-section">
                <div className="dt-sb-section-head">
                  <span className="dt-sb-section-lbl">Patient Notes</span>
                  {parsedSummary?.patientTodos && (
                    <button
                      className="dt-copy-inline"
                      onClick={() => copyTextarea("notes", "dt-patient-notes")}
                    >
                      {copyStates["notes"] ? "Copied" : "Copy"}
                    </button>
                  )}
                </div>
                <textarea
                  className="dt-notes-ta"
                  id="dt-patient-notes"
                  rows={4}
                  value={parsedSummary?.patientTodos ?? ""}
                  placeholder={visitState === "done"
                    ? "No patient notes were identified for this visit."
                    : "Patient notes will appear here after the visit ends."}
                  readOnly
                />
                <button className="dt-btn-send dt-btn-outline" disabled={!parsedSummary?.patientTodos}>
                  Send to Patient
                </button>
              </div>

              <div className="dt-rule" />

              {/* Coding */}
              <div className="dt-sb-section">
                <div className="dt-sb-section-head">
                  <span className="dt-sb-section-lbl">Coding</span>
                  {codingData && codingData.codes.length > 0 && (
                    <button
                      className="dt-copy-inline"
                      onClick={() => copySection("coding", "dt-goz-codes")}
                    >
                      {copyStates["coding"] ? "Copied" : "Copy"}
                    </button>
                  )}
                </div>
                {isGeneratingCoding ? (
                  <p className="dt-summary-p" style={{ color: "var(--ink-4)" }}>
                    Generating codes…
                  </p>
                ) : codingData && codingData.codes.length > 0 ? (
                  <>
                    <div className="dt-goz-codes" id="dt-goz-codes">
                      {codingData.codes.map((c) => (
                        <div key={c.code} className="dt-goz-row">
                          <span className="dt-goz-code">{c.code}</span>
                          <div>
                            <span className="dt-goz-desc">{c.description}</span>
                            {c.reason && (
                              <p className="dt-goz-reason">{c.reason}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="dt-goz-actions">
                      <button className="dt-btn-sm dt-btn-sm-ghost">+ Add Code</button>
                      <button className="dt-btn-sm dt-btn-sm-gold">Confirm Billing</button>
                    </div>
                  </>
                ) : (
                  <p className="dt-summary-p" style={{ color: "var(--ink-4)" }}>
                    {codingData && codingData.codes.length === 0
                      ? "No treatment codes were identified for this visit."
                      : "Treatment codes will be generated automatically once the visit is complete."}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
